'use client';

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material';

/**
 * Phase D — when the user manages **multiple** Facebook Pages, optionally allowlist which ones feed vendor Inbox.
 * Preference: `User.notificationPreferences.inboxVendorFacebookPageIds` (see `inboxFacebookPagePreference.ts`).
 */
export function InboxFacebookPagesCard() {
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState<Array<{ id: string; name: string }>>([]);
  const [allowlistPageIds, setAllowlistPageIds] = useState<string[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const r = await fetch('/api/user/inbox-facebook-pages', { credentials: 'include' });
      const j = (await r.json()) as {
        connected?: boolean;
        pages?: Array<{ id: string; name: string }>;
        allowlistPageIds?: string[] | null;
        error?: string;
      };
      if (!r.ok) {
        setPages([]);
        setMessage(j.error ?? 'Could not load Facebook Pages.');
        return;
      }
      if (!j.connected) {
        setPages([]);
        setAllowlistPageIds(null);
        return;
      }
      setPages(Array.isArray(j.pages) ? j.pages : []);
      setAllowlistPageIds(j.allowlistPageIds ?? null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (pages.length === 0) return;
    if (allowlistPageIds?.length) {
      setSelected(new Set(allowlistPageIds));
    } else {
      setSelected(new Set(pages.map((p) => p.id)));
    }
  }, [pages, allowlistPageIds]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const save = async (pageIds: string[] | null) => {
    setSaving(true);
    setMessage(null);
    try {
      const r = await fetch('/api/user/inbox-facebook-pages', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pageIds }),
      });
      const j = (await r.json().catch(() => ({}))) as { error?: string; allowlistPageIds?: string[] | null };
      if (!r.ok) {
        setMessage(j.error ?? 'Save failed.');
        return;
      }
      setAllowlistPageIds(j.allowlistPageIds ?? null);
      setMessage('Saved. Open Inbox and tap Sync connected networks to refresh Facebook comments.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSelection = async () => {
    if (pages.length === 0) return;
    const allSelected =
      selected.size === pages.length && pages.every((p) => selected.has(p.id));
    if (!allSelected && selected.size === 0) {
      setMessage('Select at least one Page, or use “All Pages for inbox”.');
      return;
    }
    await save(allSelected ? null : Array.from(selected));
  };

  const useAllPages = async () => {
    setSelected(new Set(pages.map((p) => p.id)));
    await save(null);
  };

  if (loading) {
    return null;
  }
  if (pages.length <= 1) {
    return null;
  }

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Inbox — Facebook Pages
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You manage multiple Pages. Choose which ones contribute <strong>Page comment</strong> rows to{' '}
          <strong>/dashboard/inbox</strong>. Leave all selected to use every Page (up to the server cap).
        </Typography>
        {message ? (
          <Alert
            severity={message.startsWith('Saved') ? 'success' : 'info'}
            sx={{ mb: 2 }}
            onClose={() => setMessage(null)}
          >
            {message}
          </Alert>
        ) : null}
        <FormGroup sx={{ mb: 2 }}>
          {pages.map((p) => (
            <FormControlLabel
              key={p.id}
              control={<Checkbox checked={selected.has(p.id)} onChange={() => toggle(p.id)} />}
              label={`${p.name} (${p.id})`}
            />
          ))}
        </FormGroup>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="contained" size="small" disabled={saving} onClick={() => void handleSaveSelection()}>
            Save selection
          </Button>
          <Button variant="outlined" size="small" disabled={saving} onClick={() => void useAllPages()}>
            All Pages for inbox
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
